import { ShieldCheck, Smartphone, X } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui/elements/button/Button';
import { useResendOtpMutation } from '@/features/auth/api/authApi';
import { useOtpCooldown } from '@/shared/hooks/useOtpCooldown';
import toast from 'react-hot-toast';

type TwoFactorDialogProps = {
  onClose: () => void;
  onVerify: (code: string) => void | Promise<void>;
  username: string;
  onOtpIdUpdate: (otpId: string) => void;
  instructionText?: string;
  methodBadgeLabel?: string;
};

export default function TwoFactorDialog({
  onClose,
  onVerify,
  username,
  onOtpIdUpdate,
  instructionText,
  methodBadgeLabel,
}: TwoFactorDialogProps) {
  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation('login');
  const [resendOtp, { isLoading: isResending }] = useResendOtpMutation();
  const COOLDOWN_STORAGE_KEY = `otp_resend_cooldown_${username}`;
  const {
    resendCooldownSeconds,
    setResendCooldownSeconds,
    handleResendError,
    handleClose: clearCooldown,
  } = useOtpCooldown(COOLDOWN_STORAGE_KEY);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleResendOtp = async () => {
    if (!username) return;

    try {
      const response = await resendOtp({ username }).unwrap();

      if (response?.otpId) {
        onOtpIdUpdate(response.otpId);
      }
      setOtp(Array(OTP_LENGTH).fill(''));
      inputRef.current?.focus();

      // Start cooldown
      const retrySeconds = response.retryAfterSeconds || 300;
      setResendCooldownSeconds(retrySeconds);
      localStorage.setItem(
        COOLDOWN_STORAGE_KEY,
        (Date.now() + retrySeconds * 1000).toString()
      );
      toast.success('OTP resent successfully');
    } catch (error: unknown) {
      const { message } = handleResendError(error);
      toast.error(message);
    }
  };

  // Focus hidden input when clicking container
  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const newOtp = Array(OTP_LENGTH).fill('');

    for (let i = 0; i < OTP_LENGTH; i++) {
      newOtp[i] = value[i] || '';
    }

    setOtp(newOtp);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];

      for (let i = otp.length - 1; i >= 0; i--) {
        if (otp[i] !== '') {
          newOtp[i] = '';
          break;
        }
      }

      setOtp(newOtp);
      e.preventDefault();
    }
  };

  const handleVerifyClick = () => {
    const code = otp.join('');
    if (code.length !== OTP_LENGTH) return;
    // Clear OTP boxes immediately after clicking verify
    setOtp(Array(OTP_LENGTH).fill(''));
    inputRef.current?.focus();

    onVerify(code);
  };

  const handleClose = () => {
    clearCooldown();
    onClose();
  };

  const activeIndex = otp.findIndex((d) => d === '');
  const showCursorIndex = activeIndex === -1 ? OTP_LENGTH - 1 : activeIndex;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px]"></div>

      {/* Dialog */}
      <div
        className="fixed top-1/2 left-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-surface p-6 shadow-lg"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="flex flex-col gap-2 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-lt-slate-900 rounded-full flex items-center justify-center">
              <ShieldCheck size={32} className="text-white" />
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-lt-slate-900">
            {t('TwoFactorAuthentication')}
          </h2>

          <p className="text-sm text-lt-slate-500">
            {instructionText ?? t('enter6')}
          </p>

          <div className="flex justify-center gap-2 pt-3">
            {methodBadgeLabel ? (
              <span className="inline-flex items-center gap-1 rounded-full border bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                <Smartphone className="w-3 h-3" />
                {methodBadgeLabel}
              </span>
            ) : null}

            {/* <span className="hidden inline-flex items-center gap-1 rounded-full border bg-green-50 px-2 py-0.5 text-xs text-green-700">
              <Fingerprint className="w-3 h-3" />
              {t('Biometric')}
            </span> */}
          </div>
        </div>

        {/* OTP */}
        <div className="space-y-5 py-4">
          <div className="flex justify-center">
            <div
              className="relative flex gap-1 cursor-text"
              onClick={handleContainerClick}
            >
              {otp.map((digit, index) => (
                <div
                  key={index}
                  data-active={showCursorIndex === index}
                  className="relative flex h-12 w-12 items-center justify-center border border-slate-300 text-sm"
                >
                  {digit}
                  {showCursorIndex === index && (
                    <span className="absolute h-6 w-px bg-black animate-blink" />
                  )}
                </div>
              ))}

              <input
                ref={inputRef}
                inputMode="numeric"
                maxLength={OTP_LENGTH}
                value={otp.join('')}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="absolute inset-0 opacity-0"
              />
            </div>
          </div>

          <div className="text-center text-sm text-slate-600">
            {resendCooldownSeconds > 0 ? (
              <span className="ml-2 text-slate-500">
                Resend OTP in ({Math.floor(resendCooldownSeconds / 60)}:
                {String(resendCooldownSeconds % 60).padStart(2, '0')})
              </span>
            ) : (
              <>
                {t('noCodeReceived')}
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isResending}
                  className="text-blue-600 font-semibold hover:underline cursor-pointer disabled:opacity-50 !cursor-pointer"
                >
                  {t('resendCode')}
                </button>
              </>
            )}
          </div>

          <Button
            variant="verify"
            className="h-14 w-full"
            disabled={otp.includes('')}
            onClick={handleVerifyClick}
          >
            {t('VerifyAndContinue')}
          </Button>

          <p className="text-xs text-center text-slate-500">
            {t('encryptionNotice')}
          </p>
        </div>

        {/* Close */}
        <button
          type="button"
          className="absolute top-4 right-4 opacity-70 cursor-pointer hover:opacity-100"
          onClick={handleClose}
        >
          <X />
        </button>

        <style>{`
          @keyframes blink {
            0%, 50%, 100% { opacity: 1; }
            25%, 75% { opacity: 0; }
          }
          .animate-blink {
            animation: blink 1s step-start infinite;
          }
        `}</style>
      </div>
    </>
  );
}
